import { useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { DangerButton, SecondaryButton } from '../../../components/buttons';
import { Loading, ThinLoadingBar } from '../../../components/loading';
import { Modal } from '../../../components/modal';
import { isOrderEditV2Enabled } from '../../../helpers/feature-flags';
import { useOrders } from '../../../hooks/orders';
import { useProducts } from '../../../hooks/products';
import { useVendor } from '../../../hooks/vendors';
import { orderEditRoute, storeLayout } from '../../../route-apis';
import { BuyupView } from './components/buyup-view';
import { OrderView } from './components/order-view';
import { SyncIndicator } from './components/sync-indicator';
import { useEditOrderView } from './hooks/use-edit-order-view';
import { useOrderItems } from './hooks/use-order-items';
import { useOrderItemsV2 } from './hooks/use-order-items-v2';
import type { EditOrderPopulatedProperties } from './types';

export function EditOrder() {
  const navigate = useNavigate();
  const { store } = storeLayout.useRouteContext();
  const { orderId } = orderEditRoute.useParams();
  const { orders } = useOrders();
  const order = useMemo(() => orders.find((order) => order.id === orderId), [orders, orderId]);
  const { vendor } = useVendor(order?.vendor_id);
  const { products } = useProducts();
  const vendorProducts = useMemo(() => {
    if (!products || !vendor) {
      return [];
    }

    return products.filter((product) => product.vendor_id === vendor.vendor_id);
  }, [products, vendor]);

  const canEdit =
    order !== undefined &&
    order.store_id === store.id &&
    vendor !== undefined &&
    products.length > 0;

  // Bounce to the order list when the order can't be edited — most often right
  // after cancelling it, which drops it from the orders cache while this route
  // is still mounted.
  //
  // This must fire exactly once. Every router state change re-renders us while
  // the navigation is in flight, and each new navigation cancels the pending
  // one, so a redirect that repeats on every render never settles: the route
  // stays mounted, re-renders, redirects again, and the page locks up. Hence
  // an effect with stable deps rather than <Navigate>, which re-navigates
  // whenever its props object identity changes — i.e. on every render.
  useEffect(() => {
    if (!canEdit) {
      navigate({ to: '/stores/$storeId/orders', params: { storeId: store.id }, replace: true });
    }
  }, [canEdit, navigate, store.id]);

  if (!canEdit) {
    return <Loading />;
  }

  return (
    <EditOrderPopulated order={order} vendor={vendor} products={vendorProducts} store={store} />
  );
}

function EditOrderPopulated(properties: EditOrderPopulatedProperties) {
  // The ONE isOrderEditV2Enabled call in the whole page: React forbids
  // conditional hook calls, so the fork can't be `flag ? useA() : useB()`
  // inline, and calling both is out (v1's hook runs a 15s poll + a 300ms
  // seed). Selecting the function here and passing it into useEditOrderView
  // is legal under the rules of hooks only because the hook identity is
  // constant for the component's lifetime — so it must actually BE constant,
  // not merely assumed to be: `properties.store` is live route-context data
  // (storeLayout.useRouteContext(), backed by a React Query cache that IS
  // invalidated mid-session — see StoreDisabledListener in router.tsx), so
  // reading it fresh on every render risks `useItems` switching hooks between
  // renders of this same mounted instance if the flag ever flips for a store
  // an employee has open, which throws ("Rendered fewer/more hooks than
  // expected") since v1 and v2 call entirely different hook sets. Freezing
  // the choice in state at mount makes the comment's claim true instead of
  // merely assumed.
  const [useItems] = useState(() =>
    isOrderEditV2Enabled(properties.store) ? useOrderItemsV2 : useOrderItems
  );
  const view = useEditOrderView(properties, useItems);

  // Block only while the initial seed is running. Don't also gate on
  // `orderItems.length === 0`: an order that legitimately has no items (e.g. a
  // draft whose vendor's products were all reassigned) would otherwise spin
  // forever, leaving the user unable to open — and therefore cancel — it.
  if (view.calculating) {
    return <Loading />;
  }

  const View = view.isBuyup ? BuyupView : OrderView;

  return (
    <>
      <div className="grid grid-cols-3 items-center px-4 py-2">
        <div />
        <h1 className="text-2xl font-semibold justify-self-center">{view.vendor.name} Order</h1>
        <SyncIndicator sync={view.sync} />
      </div>
      <View view={view} />
      {/* Driven by `visible`, never by a mount guard: `Modal` wraps a Headless UI
          Transition, and unmounting it outright skips the leave animation and
          tears the Dialog, its Portal, and the nested DemoCTAModal down in one
          synchronous commit instead of letting Headless UI close them in order. */}
      <Modal
        title="Purchase Order"
        visible={view.showSubmitOrder}
        onClose={view.toggleShowSubmitOrder}
        footer={view.review.footer}
      >
        {view.review.body}
      </Modal>
      <Modal
        title="Cancel Order"
        visible={view.showCancelConfirm}
        onClose={view.dismissCancelConfirm}
        size="sm"
        footer={
          <>
            <SecondaryButton onClick={view.dismissCancelConfirm}>Keep Order</SecondaryButton>
            <DangerButton onClick={view.confirmDeleteOrder}>Cancel Order</DangerButton>
          </>
        }
      >
        <p className="text-sm text-gray-600">
          Are you sure you want to cancel this order? This cannot be undone.
        </p>
      </Modal>
      <Modal
        title="Reset Order"
        visible={view.showResetConfirm}
        onClose={view.dismissResetConfirm}
        size="sm"
        footer={
          <>
            <SecondaryButton onClick={view.dismissResetConfirm}>Keep Changes</SecondaryButton>
            <DangerButton onClick={view.confirmResetOrder}>Reset Order</DangerButton>
          </>
        }
      >
        <p className="text-sm text-gray-600">
          This will reset changes you have made to {view.resetItemCount} item(s) this order. Are you
          sure you want to continue?
        </p>
      </Modal>
      {view.productSaving && <ThinLoadingBar />}
    </>
  );
}
